import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import Document from '@tiptap/extension-document';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import Mention from '@tiptap/extension-mention';
import { ReactRenderer } from '@tiptap/react';
import tippy from 'tippy.js';

// Resolve value from key path
// function getAllPaths(obj, currentPath = '@') {
//   let paths = [];

//   if (Array.isArray(obj)) {
//     obj.forEach((item, index) => {
//       const itemPath = `${currentPath}[${index}]`;
//       paths.push({ id: itemPath, label: itemPath });
//       paths = paths.concat(getAllPaths(item, itemPath));
//     });
//   } else if (typeof obj === 'object' && obj !== null) {
//     Object.keys(obj).forEach(key => {
//       const keyPath = currentPath === '@' ? `${key}` : `${currentPath}.${key}`;
//       paths.push({ id: keyPath, label: keyPath });
//       paths = paths.concat(getAllPaths(obj[key], keyPath));
//     });
//   }

//   return paths;
// }
function getAllPaths(obj, currentPath = '@') {
  let paths = [];

  if (Array.isArray(obj)) {
    obj.forEach((item, index) => {
      const itemPath = `${currentPath}[${index}]`;
      if (item !== null && typeof item === 'object') {
        paths = paths.concat(getAllPaths(item, itemPath));
      } else {
        paths.push({ id: itemPath, label: itemPath });
      }
    });
  } else if (typeof obj === 'object' && obj !== null) {
    Object.keys(obj).forEach(key => {
      const keyPath = currentPath === '@' ? `${key}` : `${currentPath}.${key}`;
      if (obj[key] !== null && typeof obj[key] === 'object') {
        paths = paths.concat(getAllPaths(obj[key], keyPath));
      } else {
        paths.push({ id: keyPath, label: keyPath });
      }
    });
  }

  return paths;
}

function getArrayPaths(obj, currentPath = '@', seenPaths = new Set()) {
  let paths = [];
  if (Array.isArray(obj)) {
    const pathEntry = { id: currentPath, label: currentPath };
    if (!seenPaths.has(currentPath)) {
      seenPaths.add(currentPath);
      // paths.push(pathEntry);
       paths.push({ id: pathEntry, label: pathEntry });
    }
    obj.forEach((item, index) => {
      const itemPath = `${currentPath}[${index}]`;
      paths = paths.concat(getArrayPaths(item,  { id: itemPath, label: itemPath }, seenPaths));
    });
  } else if (typeof obj === 'object' && obj !== null) {
    Object.keys(obj).forEach(key => {
      const keyPath = currentPath === '@' ? key : `${currentPath}.${key}`;
      paths = paths.concat(getArrayPaths(obj[key], { id: keyPath, label: keyPath }, seenPaths));
    });
  }
  return paths;
}
// Main Editor Component
const CustomTextEditor = ({ handleTitleChange, prompt, disabled, sampleData, primaryPath = "",arrayOnly=false }) => {
  // console.log("🚀 ~ CustomTextEditor ~ prompt:", prompt)
  const limit = 280;
  let mentionTooltip = null;

  // Recursively generate value from key path
  function getValueFromPath(path, data) {
    try {
      const cleanPath = path.startsWith('@') ? path.slice(1) : path;
      const keys = cleanPath.replace(/\[(\d+)\]/g, '.$1').split('.');
      let value = data;
      for (const key of keys) {
        value = value[key];
        if (value === undefined || value === null) return 'No value found';
      }
      return JSON.stringify(value, null, 2);
    } catch {
      return 'No value found';
    }
  }

  // Mention Dropdown List
  const MentionList = forwardRef((props, ref) => {
    const [selectedIndex, setSelectedIndex] = useState(0);
    let tooltipInstance = null;
    const buttonRefs = useRef([]);

    const selectItem = (index) => {
      const item = props?.items?.[index];
      if (item) {
        props.command({ id: item.id, label: item.label + ' ' });
      }
    };

    const upHandler = () => setSelectedIndex((selectedIndex + props.items.length - 1) % props.items.length);
    const downHandler = () => setSelectedIndex((selectedIndex + 1) % props.items.length);
    const enterHandler = () => selectItem(selectedIndex);

    const showTooltip = (element, item) => {
      if (tooltipInstance) tooltipInstance.destroy();
      const value = getValueFromPath(item.id, sampleData);
      tooltipInstance = tippy(element, {
        content: `<pre style="background: #f4f4f4; padding: 8px; border-radius: 4px; max-width: 300px; max-height: 200px; overflow: auto; margin: 0;">${value}</pre>`,
        allowHTML: true,
        placement: 'right',
        theme: 'light',
      });
      tooltipInstance.show();
    };

    const hideTooltip = () => {
      if (tooltipInstance) {
        tooltipInstance.destroy();
        tooltipInstance = null;
      }
    };

    useEffect(() => {
      setSelectedIndex(0);
    }, [props.items]);

    useImperativeHandle(ref, () => ({
      onKeyDown: ({ event }) => {
        if (!props.items.length) return false;
        if (event.key === 'ArrowUp') { upHandler(); return true; }
        if (event.key === 'ArrowDown') { downHandler(); return true; }
        if (event.key === 'Enter') { enterHandler(); return true; }
        return false;
      },
    }));

    useEffect(() => {
      if (buttonRefs.current[selectedIndex] && props.items[selectedIndex]) {
        showTooltip(buttonRefs.current[selectedIndex], props.items[selectedIndex]);
      }
      return hideTooltip;
    }, [selectedIndex, props.items]);

    return (
      <div style={{ 
        background: '#fff', 
        border: '1px solid #ccc', 
        borderRadius: '4px', 
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)', 
        maxHeight: '200px', 
        overflowY: 'auto', 
        padding: '5px',
        width: '300px'
      }}>
        {props.items.length ? (
          props.items.map((item, index) => (
            <button type='button'
              ref={(el) => (buttonRefs.current[index] = el)}
              style={{
                display: 'block',
                width: '100%',
                padding: '8px 12px',
                textAlign: 'left',
                backgroundColor: index === selectedIndex ? '#e0e0e0' : '#fff',
                border: 'none',
                cursor: 'pointer',
                fontSize: '14px',
                color: '#333',
              }}
              key={item.id}
              onClick={() => selectItem(index)}
              onMouseEnter={(e) => showTooltip(e.currentTarget, item)}
              onMouseLeave={hideTooltip}
            >
              {item.label}
            </button>
          ))
        ) : (
          <div style={{ padding: '8px 12px', color: '#666', fontSize: '14px' }}>No suggestions</div>
        )}
      </div>
    );
  });

  // Suggestion Logic
  const suggestion = {
    items: ({ query, editor }) => {
      // const allPaths = arrayOnly ? getArrayPaths(sampleData):getAllPaths(sampleData);
      const allPaths = getAllPaths(sampleData);



      const { selection } = editor.state;
      const { from } = selection;

      const textBefore = editor.state.doc.textBetween(Math.max(0, from - 50), from, '\n', '');
      const lastAtIndex = textBefore.lastIndexOf('@');
      
      let cleanQuery = '';
      if (lastAtIndex !== -1) {
        cleanQuery = textBefore.slice(lastAtIndex + 1).trim();
      } else if (query) {
        cleanQuery = query.startsWith('@') ? query.slice(1) : query;
      }

      const pos = from > 0 ? from - 1 : from;
      const nodeAtPos = editor.view.domAtPos(pos)?.node;
      const prevNode = nodeAtPos?.previousSibling || nodeAtPos;

      if (prevNode?.dataset?.type === 'mention') {
        const mentionId = prevNode.dataset.id;
        const nextSuggestions = allPaths
          .filter(item => item.label.startsWith(mentionId) && item.label !== mentionId)
          .map(item => ({
            id: item.id,
            label: item.label.replace(mentionId, '') || item.label
          }))
          .filter(item => item.label && item.label.toLowerCase().includes(cleanQuery.toLowerCase()))
          // .slice(0, 20);

        if (nextSuggestions.length) return nextSuggestions;
      }

      return allPaths
        .filter(item => item.label.toLowerCase().includes(cleanQuery.toLowerCase()))
        .sort((a, b) => {
          const aStarts = a.label.toLowerCase().startsWith(cleanQuery.toLowerCase());
          const bStarts = b.label.toLowerCase().startsWith(cleanQuery.toLowerCase());
          if (aStarts && !bStarts) return -1;
          if (!aStarts && bStarts) return 1;
          return a.label.length - b.label.length;
        })
        // .slice(0, 20);
    },
    char: '@',
    startOfLine: false,
    allowSpaces: false,
    allowedPrefixes: null,
    render: () => {
      let reactRenderer;
      let popup;

      return {
        onStart: (props) => {
          if (!props.clientRect) return;

          reactRenderer = new ReactRenderer(MentionList, { props, editor: props.editor });

          popup = tippy('body', {
            getReferenceClientRect: props.clientRect,
            appendTo: () => document.body,
            content: reactRenderer.element,
            showOnCreate: true,
            interactive: true,
            trigger: 'manual',
            placement: 'bottom-start',
          });
        },
        onUpdate(props) {
          if (reactRenderer) reactRenderer.updateProps(props);
          if (!props.clientRect || !popup) return;

          popup[0].setProps({ getReferenceClientRect: props.clientRect });
        },
        onKeyDown(props) {
          if (props.event.key === 'Escape') {
            if (popup && popup[0]) popup[0].hide();
            return true;
          }
          return reactRenderer?.ref?.onKeyDown(props) || false;
        },
        onExit() {
          if (popup && popup[0]) popup[0].destroy();
          if (reactRenderer) reactRenderer.destroy();
        },
      };
    },
  };

  // Render mentions and text as HTML
  const renderContent = (data) => {
    // console.log("🚀 ~ renderContent ~ data:", data)
    let jsonData;
    if (typeof data === 'string') {
      try { jsonData = JSON.parse(data); } 
      catch {
        // console.error("Invalid JSON data:", data);
        return `<p style="color: red;">${data || ''}</p>`;
      }
    } else if (Array.isArray(data)) jsonData = data;
    else return `<p>${data || ''}</p>`;
    if(!Array.isArray(jsonData)){
return `<p>${JSON.stringify(jsonData) || ''}</p>`
    }
    return `<p>${jsonData.map(item =>
      item.type === 'mention'
        ? `<span style="background: #e6f3ff; color: #1a73e8; padding: 2px 4px; border-radius: 4px; font-weight: 500;" data-type="mention" data-id="${item.attrs.id}" data-label="${item.attrs.label}" contenteditable="false">${item.attrs.label}</span>`
        : item.type === 'text' ? item.text.trim() : ''
    ).join('')}</p>`;
  };

  const editor = useEditor({
    extensions: [
      Document,
      Paragraph.configure({
        HTMLAttributes: {
          style: 'margin: 0; padding: 0; font-size: 16px; line-height: 1.5; height: 100%; width: 100%; overflow: auto; white-space: pre-wrap; word-break: break-all;',
        },
      }),
      Text,
      Mention.configure({
        HTMLAttributes: { 
          style: 'background: #e6f3ff; color: #1a73e8; padding: 2px 4px; border-radius: 4px; font-weight: 500;' 
        },
        suggestion,
      }),
    ],
    content: renderContent(prompt),
    onTransaction({ transaction }) {
      // const data = transaction?.doc?.content?.toJSON()?.[0]?.content;

      // if (typeof handleTitleChange === 'function' && data && prompt !== JSON.stringify(data)) {

      //   handleTitleChange({
      //     target: {
      //       name: 'prompt',
      //       value: data ? JSON.stringify(data) : null,
      //     },
      //   });
      // }

      let data = []
      const customiseddata = transaction?.doc?.content?.toJSON()?.map(k=>{
        let content = k.content
        if(Array.isArray(content)){
          if(content[0]){
            data.push(...content)
          }
        }
      })

      // console.log("🚀 ~ onTransaction ~ data:", data)

      if (typeof handleTitleChange === 'function' && data && data[0]&& prompt !== JSON.stringify(data)) {

        handleTitleChange({
          target: {
            name: 'prompt',
            value: data ? JSON.stringify(data) : null,
          },
        });
      }
    },
    onCreate({ editor }) {
      const editorElement = editor.view.dom;
      editorElement.addEventListener('mouseover', (e) => {
        if (e.target.dataset.type === 'mention') {
          const id = e.target.dataset.id;
          const value = getValueFromPath(id, sampleData);
          if (mentionTooltip) mentionTooltip.destroy();
          mentionTooltip = tippy(e.target, {
            content: `<pre style="background: #f4f4f4; padding: 8px; border-radius: 4px; max-width: 300px; max-height: 200px; overflow: auto; margin: 0;">${value}</pre>`,
            allowHTML: true,
            placement: 'top',
            theme: 'light',
          });
          mentionTooltip.show();
        }
      });
      editorElement.addEventListener('mouseout', () => {
        if (mentionTooltip) {
          mentionTooltip.destroy();
          mentionTooltip = null;
        }
      });
      editorElement.addEventListener('click', (e) => {
        if (e.target.dataset.type === 'mention') {
          const id = e.target.dataset.id;
          const value = getValueFromPath(id, sampleData);
          if (mentionTooltip) mentionTooltip.destroy();
          mentionTooltip = tippy(e.target, {
            content: `<pre style="background: #f4f4f4; padding: 8px; border-radius: 4px; max-width: 300px; max-height: 200px; overflow: auto; margin: 0;">${value}</pre>`,
            allowHTML: true,
            placement: 'top',
            theme: 'light',
          });
          mentionTooltip.show();
        }
      });
    },
    onSelectionUpdate({ editor }) {
      const { selection } = editor.state;
      const { from, to } = selection;
      const node = editor.view.domAtPos(from < to ? from : to)?.node;
      const mentionNode = node?.previousSibling?.dataset?.type === 'mention' ? node.previousSibling : node?.dataset?.type === 'mention' ? node : null;
      if (mentionNode) {
        const id = mentionNode.dataset.id;
        const value = getValueFromPath(id, sampleData);
        if (mentionTooltip) mentionTooltip.destroy();
        mentionTooltip = tippy(mentionNode, {
          content: `<pre style="background: #f4f4f4; padding: 8px; border-radius: 4px; max-width: 300px; max-height: 200px; overflow: auto; margin: 0;">${value}</pre>`,
          allowHTML: true,
          placement: 'top',
          theme: 'light',
        });
        mentionTooltip.show();
      } else {
        if (mentionTooltip) {
          mentionTooltip.destroy();
          mentionTooltip = null;
        }
      }
    },
    editable: !disabled,
  }, [sampleData]);

  // Update editor content when prompt changes
  // useEffect(() => {
  //   if (editor ) {
     
  //     editor?.commands?.setContent(renderContent(prompt));
  //   }
  // }, [editor, prompt]);
useEffect(() => {
  if (editor) {
    // Store the current cursor position
    const { from, to } = editor.state.selection;

    // Update the content
    editor.commands.setContent(renderContent(prompt));

    // Restore the cursor position
    editor.commands.setTextSelection({ from, to });
  }
}, [editor, prompt]);
  return (
    <div style={{
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    }}>
      <style>
    {`
      
        div[contenteditable="true"][role="textbox"] {
  outline: none;
  border: 1px solid #ccc;
  padding: 10px;
  font-size: 16px;
  line-height: 1.5;
  width: 100%;
  min-height: 100%;
  white-space: pre-wrap;
  word-break: break-word;
  overflow-y: auto;
  border-radius: 8px;
  position:relative
}
    `}
  </style>
      <EditorContent
        editor={editor}
        style={{
          width: 'auto',
          height: '100%',
         
          boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
          borderRadius: '0.375rem',
          overflow: 'auto',
      
          transition: 'border-color 0.2s',
          wordBreak: 'break-all'
        }}
        onMouseEnter={(e) => e.currentTarget.style.borderColor = '#6b21a8'}
        onMouseLeave={(e) => e.currentTarget.style.borderColor = '#d1d5db'}
        onFocus={(e) => e.currentTarget.style.borderColor = '#6b21a8'}
        onBlur={(e) => e.currentTarget.style.borderColor = '#d1d5db'}
        placeholder="Type @ to see suggestions"
      />
    </div>
  );
};

export default CustomTextEditor;